<div class="w-full">
    <header class="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div class="min-w-0 flex-[1_1_16rem]">
            <h1 class="m-0 text-xl font-bold leading-7 text-slate-900">Stagioni e categorie</h1>
            <p class="m-0 mt-1 max-w-2xl text-sm leading-6 text-slate-500">
                Scegli una stagione per gestirne categorie e quote. La stagione <b>corrente</b> è quella usata
                per le nuove iscrizioni.
            </p>
        </div>
        <button
            *ngIf="seasons.length > visibleSeasons.length"
            type="button"
            class="btn welldone-btn-gray inline-flex shrink-0 items-center gap-1.5"
            (click)="openAllSeasons()"
        >
            <i class="bi bi-list-ul" aria-hidden="true"></i>
            Tutte le stagioni
            <span class="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-bold text-slate-600">{{ seasons.length }}</span>
        </button>
    </header>

    <!-- Seasons -->
    <section *ngIf="loadingSeasons || seasons.length" class="mb-10 grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-4" aria-label="Stagioni">
        <ng-container *ngIf="!loadingSeasons; else seasonsSkeleton">
            <article
                *ngFor="let season of visibleSeasons; trackBy: trackById"
                class="flex min-h-[10rem] cursor-pointer flex-col rounded-2xl border-2 bg-white px-5 py-4 shadow-sm transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-soft"
                [ngClass]="season.id === selectedSeasonId ? 'border-primary shadow-md' : 'border-slate-100 hover:border-slate-300'"
                role="button"
                tabindex="0"
                [attr.aria-pressed]="season.id === selectedSeasonId"
                (click)="selectSeason(season)"
                (keydown.enter)="selectSeason(season)"
                (keydown.space)="$event.preventDefault(); selectSeason(season)"
            >
                <div class="mb-3 flex items-center justify-between">
                    <span class="inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-[0.7rem] font-bold uppercase tracking-wide"
                          [ngClass]="seasonPhaseClasses(season)">
                        <i *ngIf="season.isCurrent" class="bi bi-check-circle-fill" aria-hidden="true"></i>
                        {{ seasonPhaseLabel(season) }}
                    </span>
                    <button
                        type="button"
                        class="inline-flex h-8 w-8 items-center justify-center rounded-lg border-0 bg-transparent text-slate-400 transition hover:bg-slate-100 hover:text-primary"
                        title="Modifica stagione"
                        aria-label="Modifica stagione"
                        (click)="$event.stopPropagation(); openSeasonModal(season)"
                    >
                        <i class="bi bi-pencil" aria-hidden="true"></i>
                    </button>
                </div>

                <h2 class="m-0 break-words text-lg font-bold leading-snug text-slate-900">{{ season.name }}</h2>
                <p class="mb-5 mt-1 flex items-center gap-1.5 text-[0.8rem] tabular-nums text-slate-500">
                    <i class="bi bi-calendar3 text-slate-400" aria-hidden="true"></i>
                    {{ seasonPeriod(season) }}
                </p>

                <button
                    *ngIf="!season.isCurrent"
                    type="button"
                    class="mt-auto inline-flex items-center gap-1.5 self-start rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[0.8rem] font-semibold text-slate-600 transition hover:border-primary hover:text-primary"
                    (click)="$event.stopPropagation(); makeSeasonCurrent(season)"
                >
                    <i class="bi bi-star" aria-hidden="true"></i>
                    Rendi corrente
                </button>
                <p *ngIf="season.isCurrent" class="m-0 mt-auto py-1.5 text-[0.8rem] font-semibold text-emerald-800">Usata per le nuove iscrizioni</p>
            </article>

            <button type="button"
                    class="flex min-h-[10rem] flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 bg-transparent font-semibold text-slate-500 transition hover:border-primary hover:text-primary"
                    (click)="openSeasonModal()">
                <i class="bi bi-plus-circle text-2xl" aria-hidden="true"></i>
                <span>Aggiungi stagione</span>
            </button>
        </ng-container>

        <ng-template #seasonsSkeleton>
            <div *ngFor="let i of [1, 2, 3, 4]" class="flex min-h-[10rem] flex-col rounded-2xl border-2 border-slate-100 bg-white px-5 py-4 shadow-sm" aria-hidden="true">
                <div class="mb-3 flex items-center justify-between">
                    <span class="h-6 w-24 animate-pulse rounded-full bg-slate-100"></span>
                    <span class="h-8 w-8 animate-pulse rounded-lg bg-slate-100"></span>
                </div>
                <span class="h-5 w-3/5 animate-pulse rounded bg-slate-100"></span>
                <span class="mb-5 mt-2.5 h-3.5 w-5/6 animate-pulse rounded bg-slate-100"></span>
                <span class="mt-auto h-8 w-32 animate-pulse rounded-lg bg-slate-100"></span>
            </div>
            <div class="flex min-h-[10rem] flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-200 font-semibold text-slate-300" aria-hidden="true">
                <i class="bi bi-plus-circle text-2xl"></i>
                <span>Aggiungi stagione</span>
            </div>
        </ng-template>
    </section>

    <!-- Categories placeholder while the seasons are still loading -->
    <section *ngIf="loadingSeasons" aria-busy="true">
        <header class="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div>
                <span class="block h-6 w-56 animate-pulse rounded bg-slate-100"></span>
                <span class="mt-2 block h-3.5 w-44 animate-pulse rounded bg-slate-100"></span>
            </div>
            <span class="h-[50px] w-40 animate-pulse rounded-xl bg-slate-100"></span>
        </header>
        <ng-container *ngTemplateOutlet="categoriesSkeleton"></ng-container>
    </section>

    <!-- Categories of the selected season -->
    <section *ngIf="!loadingSeasons && selectedSeason as season">
        <header class="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div class="min-w-0 flex-[1_1_16rem]">
                <h2 class="m-0 flex flex-wrap items-center gap-2.5 text-lg font-bold text-slate-900">
                    Categorie · {{ season.name }}
                    <span *ngIf="!season.isCurrent" class="rounded-full bg-slate-100 px-2.5 py-1 text-[0.7rem] font-bold uppercase tracking-wide text-slate-500">
                        {{ seasonPhaseLabel(season) }}
                    </span>
                </h2>
                <span *ngIf="loadingCategories" class="mt-2 block h-3.5 w-44 animate-pulse rounded bg-slate-100"></span>
                <p *ngIf="!loadingCategories" class="m-0 mt-1 text-sm text-slate-500">
                    {{ groups.length }} {{ groups.length === 1 ? 'categoria' : 'categorie' }},
                    {{ subcategoryCount }} {{ subcategoryCount === 1 ? 'sottocategoria' : 'sottocategorie' }}
                </p>
            </div>
            <button type="button" class="btn btn-primary inline-flex shrink-0 items-center gap-1.5" (click)="openParentCategoryModal()">
                <i class="bi bi-plus-lg" aria-hidden="true"></i>
                Nuova categoria
            </button>
        </header>

        <ng-container *ngIf="loadingCategories">
            <ng-container *ngTemplateOutlet="categoriesSkeleton"></ng-container>
        </ng-container>

        <div *ngIf="!loadingCategories && !groups.length"
             class="flex flex-col items-center rounded-2xl border-2 border-dashed border-slate-200 px-6 py-12 text-center">
            <i class="bi bi-diagram-3 mb-3 text-4xl text-slate-300" aria-hidden="true"></i>
            <h3 class="m-0 text-base font-bold text-slate-900">Nessuna categoria in questa stagione</h3>
            <p class="mb-5 mt-1.5 max-w-md text-sm text-slate-500">
                Crea una categoria (es. <i>Settore giovanile</i>) e poi aggiungi le sottocategorie con le quote
                di iscrizione (es. <i>Under 14</i>).
            </p>
            <button type="button" class="btn btn-primary" (click)="openParentCategoryModal()">Crea la prima categoria</button>
        </div>

        <div *ngIf="!loadingCategories && groups.length" class="grid gap-5 sm:grid-cols-[repeat(auto-fill,minmax(30rem,1fr))]">
            <article *ngFor="let group of groups; trackBy: trackById"
                     class="flex flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
                <header class="flex items-center justify-between gap-3 border-b border-slate-100 py-4 pl-5 pr-4">
                    <div class="min-w-0">
                        <h3 class="m-0 break-words text-base font-bold text-slate-900">{{ group.category.name }}</h3>
                        <span class="text-xs font-semibold text-slate-400">
                            {{ group.children.length }} {{ group.children.length === 1 ? 'sottocategoria' : 'sottocategorie' }}
                        </span>
                    </div>
                    <div class="flex shrink-0 gap-0.5">
                        <button
                            type="button"
                            class="inline-flex h-8 w-8 items-center justify-center rounded-lg border-0 bg-transparent text-slate-400 transition hover:bg-slate-100 hover:text-primary"
                            title="Rinomina categoria"
                            aria-label="Rinomina categoria"
                            (click)="openParentCategoryModal(group.category)"
                        >
                            <i class="bi bi-pencil" aria-hidden="true"></i>
                        </button>
                        <button
                            *ngIf="!group.children.length"
                            type="button"
                            class="inline-flex h-8 w-8 items-center justify-center rounded-lg border-0 bg-transparent text-slate-400 transition hover:bg-red-50 hover:text-red-700"
                            title="Elimina categoria"
                            aria-label="Elimina categoria"
                            (click)="deleteCategory(group.category, true)"
                        >
                            <i class="bi bi-trash" aria-hidden="true"></i>
                        </button>
                    </div>
                </header>

                <ul *ngIf="group.children.length" class="m-0 list-none py-1 pl-0">
                    <li class="grid grid-cols-[minmax(0,1fr)_5.5rem_5.5rem_6.75rem] items-center gap-2 py-2 pl-5 pr-3 text-[0.7rem] font-bold uppercase tracking-wide text-slate-400" aria-hidden="true">
                        <span>Sottocategoria</span>
                        <span class="text-right">Intero</span>
                        <span class="text-right">Ridotto</span>
                        <span></span>
                    </li>
                    <li *ngFor="let child of group.children; trackBy: trackById"
                        class="grid grid-cols-[minmax(0,1fr)_5.5rem_5.5rem_6.75rem] items-center gap-2 py-1.5 pl-5 pr-3 text-sm text-slate-700 hover:bg-slate-50">
                        <a class="break-words font-semibold leading-snug text-slate-700 no-underline hover:text-primary hover:underline"
                           title="Iscritti e convocazioni"
                           [routerLink]="['/companies/subscriptions/category', child.id]">{{ child.name }}</a>
                        <span class="whitespace-nowrap text-right tabular-nums">{{ formatPrice(child.fullPrice) }}</span>
                        <span class="whitespace-nowrap text-right tabular-nums text-slate-500">
                            {{ child.reducedPrice === child.fullPrice ? '—' : formatPrice(child.reducedPrice) }}
                        </span>
                        <span class="flex justify-end gap-0.5">
                            <a
                                class="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 no-underline transition hover:bg-slate-100 hover:text-primary"
                                title="Iscritti e convocazioni"
                                aria-label="Iscritti e convocazioni"
                                [routerLink]="['/companies/subscriptions/category', child.id]"
                            >
                                <i class="bi bi-people" aria-hidden="true"></i>
                            </a>
                            <button
                                type="button"
                                class="inline-flex h-8 w-8 items-center justify-center rounded-lg border-0 bg-transparent text-slate-400 transition hover:bg-slate-100 hover:text-primary"
                                title="Modifica sottocategoria"
                                aria-label="Modifica sottocategoria"
                                (click)="openSubcategoryModal(group.category, child)"
                            >
                                <i class="bi bi-pencil" aria-hidden="true"></i>
                            </button>
                            <button
                                type="button"
                                class="inline-flex h-8 w-8 items-center justify-center rounded-lg border-0 bg-transparent text-slate-400 transition hover:bg-red-50 hover:text-red-700"
                                title="Elimina sottocategoria"
                                aria-label="Elimina sottocategoria"
                                (click)="deleteCategory(child)"
                            >
                                <i class="bi bi-trash" aria-hidden="true"></i>
                            </button>
                        </span>
                    </li>
                </ul>

                <p *ngIf="!group.children.length" class="m-0 px-5 pb-1 pt-4 text-sm text-slate-400">
                    Nessuna sottocategoria: aggiungine una per definire le quote di iscrizione.
                </p>

                <button type="button"
                        class="mx-3 mb-3.5 mt-2 inline-flex items-center gap-1 self-start rounded-lg border-0 bg-transparent px-2 py-1.5 text-sm font-semibold text-primary transition hover:bg-primary-soft"
                        (click)="openSubcategoryModal(group.category)">
                    <i class="bi bi-plus text-lg leading-none" aria-hidden="true"></i>
                    Aggiungi sottocategoria
                </button>
            </article>
        </div>
    </section>

    <div *ngIf="!loadingSeasons && !seasons.length"
         class="flex flex-col items-center rounded-2xl border-2 border-dashed border-slate-200 px-6 py-12 text-center">
        <i class="bi bi-calendar-plus mb-3 text-4xl text-slate-300" aria-hidden="true"></i>
        <h3 class="m-0 text-base font-bold text-slate-900">Nessuna stagione</h3>
        <p class="mb-5 mt-1.5 max-w-md text-sm text-slate-500">Crea la prima stagione sportiva: diventerà automaticamente quella corrente.</p>
        <button type="button" class="btn btn-primary" (click)="openSeasonModal()">Crea stagione</button>
    </div>
</div>

<ng-template #categoriesSkeleton>
    <div class="grid gap-5 sm:grid-cols-[repeat(auto-fill,minmax(30rem,1fr))]" aria-hidden="true">
        <div *ngFor="let rows of [[1, 2, 3], [1, 2], [1, 2, 3, 4], [1, 2]]"
             class="flex flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
            <div class="flex items-center justify-between gap-3 border-b border-slate-100 py-4 pl-5 pr-4">
                <div>
                    <span class="block h-4 w-36 animate-pulse rounded bg-slate-100"></span>
                    <span class="mt-2 block h-3 w-24 animate-pulse rounded bg-slate-100"></span>
                </div>
                <span class="h-8 w-8 animate-pulse rounded-lg bg-slate-100"></span>
            </div>
            <div class="py-1">
                <div *ngFor="let row of rows" class="grid grid-cols-[minmax(0,1fr)_5.5rem_5.5rem_6.75rem] items-center gap-2 py-2.5 pl-5 pr-3">
                    <span class="h-3.5 w-3/4 animate-pulse rounded bg-slate-100"></span>
                    <span class="h-3.5 w-14 animate-pulse justify-self-end rounded bg-slate-100"></span>
                    <span class="h-3.5 w-14 animate-pulse justify-self-end rounded bg-slate-100"></span>
                    <span></span>
                </div>
            </div>
            <span class="mx-5 mb-4 mt-3.5 h-4 w-44 animate-pulse rounded bg-slate-100"></span>
        </div>
    </div>
</ng-template>

<!-- Season modal (create / edit) -->
<ngx-smart-modal #seasonModal identifier="seasonModal" customClass="season-modal" [closable]="true">
    <form (ngSubmit)="saveSeason()" class="w-full">
        <header class="flex items-start gap-3.5 pl-6 pr-14 pt-5">
            <span class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-lg text-primary">
                <i class="bi bi-calendar3" aria-hidden="true"></i>
            </span>
            <div>
                <h2 class="m-0 text-[1.05rem] font-bold leading-6 text-slate-900">{{ seasonForm.id ? 'Modifica stagione' : 'Nuova stagione' }}</h2>
                <p class="m-0 mt-0.5 text-sm text-slate-500">
                    {{ seasonForm.id ? 'Aggiorna nome e periodo della stagione.' : 'Definisci il periodo sportivo per cui raccogliere le iscrizioni.' }}
                </p>
            </div>
        </header>

        <div class="flex flex-col gap-4 px-6 pb-6 pt-5">
            <div>
                <label for="seasonName" class="form-label required mb-1.5 block text-sm font-semibold text-slate-700">Nome</label>
                <input type="text" class="form-control form-control-solid" id="seasonName" name="seasonName"
                       placeholder="Es. 2026/2027" [(ngModel)]="seasonForm.name" required>
            </div>

            <div>
                <span class="form-label required mb-1.5 block text-sm font-semibold text-slate-700">Periodo</span>
                <div class="flex items-center gap-2">
                    <input type="date" class="form-control form-control-solid min-w-0 flex-1" name="seasonStart"
                           aria-label="Data di inizio" [(ngModel)]="seasonForm.startDate" required>
                    <i class="bi bi-arrow-right shrink-0 text-slate-400" aria-hidden="true"></i>
                    <input type="date" class="form-control form-control-solid min-w-0 flex-1" name="seasonEnd"
                           aria-label="Data di fine" [(ngModel)]="seasonForm.endDate" required>
                </div>
                <p *ngIf="seasonFormError; else seasonDuration" class="m-0 mt-1.5 text-[0.8rem] font-semibold text-red-700">{{ seasonFormError }}</p>
                <ng-template #seasonDuration>
                    <p *ngIf="seasonDurationLabel" class="m-0 mt-1.5 text-[0.8rem] text-slate-500">{{ seasonDurationLabel }}</p>
                </ng-template>
            </div>

            <p *ngIf="!seasonForm.id && seasons.length" class="m-0 flex gap-2 rounded-xl bg-slate-50 px-3.5 py-2.5 text-[0.8rem] leading-snug text-slate-600">
                <i class="bi bi-info-circle text-slate-400" aria-hidden="true"></i>
                La stagione corrente non cambia: potrai rendere corrente questa quando apri le iscrizioni.
            </p>
        </div>

        <footer class="flex justify-end gap-2.5 border-t border-slate-100 bg-slate-50/60 px-6 py-4">
            <button type="button" class="btn welldone-btn-gray" (click)="seasonModal.close()">Annulla</button>
            <button type="submit" class="btn btn-primary" [disabled]="!isSeasonFormValid || saving">
                {{ seasonForm.id ? 'Salva' : 'Crea stagione' }}
            </button>
        </footer>
    </form>
</ngx-smart-modal>

<!-- Category / subcategory modal (create / edit) -->
<ngx-smart-modal #categoryModal identifier="categoryModal" customClass="season-modal" [closable]="true">
    <form (ngSubmit)="saveCategory()" class="w-full">
        <header class="flex items-start gap-3.5 pl-6 pr-14 pt-5">
            <span class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-lg text-primary">
                <i class="bi" [ngClass]="categoryFormKind === 'parent' ? 'bi-folder2' : 'bi-tag'" aria-hidden="true"></i>
            </span>
            <div>
                <h2 class="m-0 text-[1.05rem] font-bold leading-6 text-slate-900">{{ categoryModalTitle }}</h2>
                <p *ngIf="selectedSeason" class="m-0 mt-0.5 text-sm text-slate-500">Stagione {{ selectedSeason.name }}</p>
            </div>
        </header>

        <div class="flex flex-col gap-4 px-6 pb-6 pt-5">
            <div>
                <label for="categoryName" class="form-label required mb-1.5 block text-sm font-semibold text-slate-700">Nome</label>
                <input type="text" class="form-control form-control-solid" id="categoryName" name="categoryName"
                       [placeholder]="categoryFormKind === 'parent' ? 'Es. Settore giovanile' : 'Es. Under 14 oppure 2012'"
                       [(ngModel)]="categoryForm.name" required>
                <p *ngIf="categoryFormKind === 'parent'" class="m-0 mt-1.5 text-[0.8rem] text-slate-500">
                    Raggruppa le sottocategorie, es. <i>Settore giovanile</i>, <i>Prima squadra</i>. Le quote si impostano sulle sottocategorie.
                </p>
            </div>

            <ng-container *ngIf="categoryFormKind === 'child'">
                <div>
                    <label for="categoryParent" class="form-label required mb-1.5 block text-sm font-semibold text-slate-700">Categoria</label>
                    <select class="form-select form-select-solid" id="categoryParent" name="categoryParent"
                            [(ngModel)]="categoryForm.parentId" required>
                        <option *ngFor="let parent of parentOptions" [ngValue]="parent.id">{{ parent.name }}</option>
                    </select>
                </div>

                <div>
                    <div class="flex flex-wrap gap-3">
                        <div class="min-w-0 flex-[1_1_9rem]">
                            <label for="categoryFullPrice" class="form-label required mb-1.5 block text-sm font-semibold text-slate-700">Quota intera</label>
                            <div class="input-group input-group-solid">
                                <span class="input-group-text">€</span>
                                <input type="number" min="0" step="0.01" class="form-control form-control-solid"
                                       id="categoryFullPrice" name="categoryFullPrice"
                                       [(ngModel)]="categoryForm.fullPrice" required>
                            </div>
                        </div>
                        <div class="min-w-0 flex-[1_1_9rem]">
                            <label for="categoryReducedPrice" class="form-label mb-1.5 block text-sm font-semibold text-slate-700">Quota ridotta</label>
                            <div class="input-group input-group-solid">
                                <span class="input-group-text">€</span>
                                <input type="number" min="0" step="0.01" class="form-control form-control-solid"
                                       id="categoryReducedPrice" name="categoryReducedPrice"
                                       [placeholder]="categoryForm.fullPrice != null ? '' + categoryForm.fullPrice : ''"
                                       [(ngModel)]="categoryForm.reducedPrice">
                            </div>
                        </div>
                    </div>
                    <p *ngIf="categoryFormError; else reducedHint" class="m-0 mt-1.5 text-[0.8rem] font-semibold text-red-700">{{ categoryFormError }}</p>
                    <ng-template #reducedHint>
                        <p class="m-0 mt-1.5 text-[0.8rem] text-slate-500">La quota ridotta vale per gli atleti con fratelli già iscritti. Vuota = nessuno sconto.</p>
                    </ng-template>
                </div>
            </ng-container>
        </div>

        <footer class="flex justify-end gap-2.5 border-t border-slate-100 bg-slate-50/60 px-6 py-4">
            <button type="button" class="btn welldone-btn-gray" (click)="categoryModal.close()">Annulla</button>
            <button type="submit" class="btn btn-primary" [disabled]="!isCategoryFormValid || saving">
                {{ categoryForm.id ? 'Salva' : 'Crea' }}
            </button>
        </footer>
    </form>
</ngx-smart-modal>

<!-- All seasons -->
<ngx-smart-modal #allSeasonsModal identifier="allSeasonsModal" customClass="season-modal is-wide" [closable]="true">
    <div class="w-full">
        <header class="flex items-start gap-3.5 pl-6 pr-14 pt-5">
            <span class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-lg text-primary">
                <i class="bi bi-list-ul" aria-hidden="true"></i>
            </span>
            <div>
                <h2 class="m-0 text-[1.05rem] font-bold leading-6 text-slate-900">Tutte le stagioni</h2>
                <p class="m-0 mt-0.5 text-sm text-slate-500">{{ seasons.length }} stagioni. Seleziona una stagione per vederne le categorie.</p>
            </div>
        </header>

        <div class="flex flex-col gap-4 px-6 pb-6 pt-5">
            <input type="search" class="form-control form-control-solid" placeholder="Cerca stagione" [(ngModel)]="seasonSearch">

            <div class="max-h-[min(28rem,60vh)] overflow-y-auto rounded-2xl border border-slate-100">
                <table class="w-full border-collapse text-sm">
                    <thead>
                        <tr>
                            <th class="sticky top-0 z-[1] bg-slate-50 px-4 py-3 text-left text-[0.7rem] font-bold uppercase tracking-wide !text-slate-500">Stagione</th>
                            <th class="sticky top-0 z-[1] bg-slate-50 px-4 py-3 text-left text-[0.7rem] font-bold uppercase tracking-wide !text-slate-500">Periodo</th>
                            <th class="sticky top-0 z-[1] bg-slate-50 px-4 py-3 text-left text-[0.7rem] font-bold uppercase tracking-wide !text-slate-500">Stato</th>
                            <th class="sticky top-0 z-[1] bg-slate-50 px-4 py-3"><span class="sr-only">Azioni</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let season of filteredSeasons; trackBy: trackById"
                            class="cursor-pointer border-t border-slate-100 transition-colors"
                            [ngClass]="season.id === selectedSeasonId ? 'bg-primary-soft' : 'hover:bg-slate-50'"
                            (click)="openSeasonFromList(season)">
                            <td class="px-4 py-2.5 font-semibold !text-slate-900">{{ season.name }}</td>
                            <td class="whitespace-nowrap px-4 py-2.5 tabular-nums">{{ seasonPeriod(season) }}</td>
                            <td class="px-4 py-2.5">
                                <span class="whitespace-nowrap rounded-full px-2.5 py-1 text-[0.7rem] font-bold uppercase tracking-wide"
                                      [ngClass]="seasonPhaseClasses(season)">{{ seasonPhaseLabel(season) }}</span>
                            </td>
                            <td class="whitespace-nowrap px-4 py-2.5 text-right" (click)="$event.stopPropagation()">
                                <button *ngIf="!season.isCurrent" type="button"
                                        class="inline-flex h-8 w-8 items-center justify-center rounded-lg border-0 bg-transparent text-slate-400 transition hover:bg-slate-100 hover:text-primary"
                                        title="Rendi corrente" aria-label="Rendi corrente"
                                        (click)="allSeasonsModal.close(); makeSeasonCurrent(season)">
                                    <i class="bi bi-star" aria-hidden="true"></i>
                                </button>
                                <button type="button"
                                        class="inline-flex h-8 w-8 items-center justify-center rounded-lg border-0 bg-transparent text-slate-400 transition hover:bg-slate-100 hover:text-primary"
                                        title="Modifica stagione" aria-label="Modifica stagione"
                                        (click)="allSeasonsModal.close(); openSeasonModal(season)">
                                    <i class="bi bi-pencil" aria-hidden="true"></i>
                                </button>
                                <button type="button"
                                        class="inline-flex h-8 w-8 items-center justify-center rounded-lg border-0 bg-transparent text-slate-400 transition hover:bg-slate-100 hover:text-primary"
                                        title="Vedi categorie" aria-label="Vedi categorie"
                                        (click)="openSeasonFromList(season)">
                                    <i class="bi bi-chevron-right" aria-hidden="true"></i>
                                </button>
                            </td>
                        </tr>
                        <tr *ngIf="!filteredSeasons.length">
                            <td colspan="4" class="px-4 py-8 text-center !text-slate-400">Nessuna stagione trovata.</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</ngx-smart-modal>
